import React, { useCallback, useMemo } from 'react';
import {
  Grid,
  Typography,
  Button,
  Paper,
  Box,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { format, isFuture, isToday, isWeekend } from 'date-fns';
import {
  BorderRadius,
  ColorPalette,
  TypographyFamily,
} from '../../../styles/constants';
import { TaskAlt } from '@mui/icons-material';
import { Holiday } from '../../../models/holidays';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import { AppTooltipContainer } from '../appTooltip';

interface Props {
  isSameMonth: boolean;
  totalHoursWorked: number;
  day: string;
  onSelectDay(date: string): void;
  dayOff?: Holiday;
  targetHoursToWork: number;
  showOnlyloggedTime?: boolean;
}

const style = {
  button: {
    display: 'flex',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    flexFlow: 'column',
    placeContent: 'baseline',
    minWidth: 0,
  },

  paperDefault: {
    backgroundColor: ColorPalette.LIGHT_GRAY,
    height: '100%',
  },
  paperHoliday: {
    backgroundColor: ColorPalette.DAY_OFF_BG_COLOR,
    height: '100%',
  },

  paperUndertime: {
    backgroundColor: ColorPalette.LIGHT_GRAY,
    border: `1px solid ${ColorPalette.WARNING_RED}`,
    height: '100%',
  },
  grid: {
    height: '75px',
  },
  employeeHoursWorkday: {
    paddingTop: '.5rem',
  },
  projectHoursBox: {
    fontFamily: TypographyFamily.NEXA,
    paddingTop: '.5rem',
  },

  dayDateTypo: {
    display: 'flex',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: '400',
    minWidth: '18px',
  },
  sameDayTypo: {
    padding: '0 .5rem',
    borderRadius: BorderRadius.SMALL,
    backgroundColor: ColorPalette.MIDDLE_BLUE,
  },

  bankHolidayMarkBox: {
    color: ColorPalette.DARK_GRAY,
    paddingTop: '.3rem',
  },
  umbrellaIcon: {
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '19px',
  },
} as const;

export function AppCalendarActivityDay({
  isSameMonth,
  totalHoursWorked,
  day,
  onSelectDay,
  dayOff,
  targetHoursToWork,
  showOnlyloggedTime = false,
}: Props): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const isWeekendOrFutureWithZeroHours = useMemo(
    () =>
      totalHoursWorked === 0 &&
      (isWeekend(Date.parse(day)) || isFuture(Date.parse(day))),
    [totalHoursWorked, day]
  );

  const onSelectingDay = useCallback(
    () => onSelectDay(day),
    [day, onSelectDay]
  );

  const bankHolidayMark = useMemo(
    () => (
      <Box sx={style.bankHolidayMarkBox}>
        <BeachAccessOutlinedIcon sx={style.umbrellaIcon} />
      </Box>
    ),
    []
  );

  const dayBtnStyle = useMemo(() => {
    if (dayOff?.dayOffDate) {
      return style.paperHoliday;
    } else if (
      !isToday(Date.parse(day)) &&
      !isWeekendOrFutureWithZeroHours &&
      (totalHoursWorked < targetHoursToWork || totalHoursWorked === 0)
    ) {
      return style.paperUndertime;
    } else {
      return style.paperDefault;
    }
  }, [
    day,
    dayOff?.dayOffDate,
    targetHoursToWork,
    totalHoursWorked,
    isWeekendOrFutureWithZeroHours,
  ]);

  return (
    <Grid item xs={1.714} sx={style.grid}>
      {!isSameMonth && (
        <Typography pt={0.5} color="#B3B3B3">
          {format(Date.parse(day), 'd')}
        </Typography>
      )}
      {isSameMonth && (
        <Paper sx={dayBtnStyle}>
          <Button sx={style.button} onClick={onSelectingDay}>
            <Typography
              sx={{
                ...style.dayDateTypo,
                ...(isToday(Date.parse(day)) && style.sameDayTypo),
              }}
              variant={isWeekend(Date.parse(day)) ? 'h2' : 'body1'}
            >
              {format(Date.parse(day), 'd')}
            </Typography>
            <>
              {!isWeekendOrFutureWithZeroHours && !showOnlyloggedTime && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {totalHoursWorked === targetHoursToWork ? (
                    <Box>
                      {isWeekend(Date.parse(day)) || dayOff ? (
                        <Typography
                          variant="warningTwo"
                          sx={style.employeeHoursWorkday}
                        >
                          {isMobile
                            ? `${totalHoursWorked}h`
                            : `${totalHoursWorked}/0h`}
                        </Typography>
                      ) : (
                        <TaskAlt color="primary" sx={{ mt: 0.5 }} />
                      )}
                    </Box>
                  ) : dayOff?.dayOffDate && totalHoursWorked === 0 ? (
                    ''
                  ) : (
                    <Typography
                      variant="warningTwo"
                      sx={style.employeeHoursWorkday}
                    >
                      {isMobile
                        ? `${totalHoursWorked}h`
                        : isWeekend(Date.parse(day)) || dayOff
                        ? `${totalHoursWorked}/0h`
                        : `${totalHoursWorked}/${targetHoursToWork}h`}
                    </Typography>
                  )}
                </Box>
              )}
            </>

            {showOnlyloggedTime && totalHoursWorked > 0 && (
              <Typography
                sx={style.projectHoursBox}
              >{`${totalHoursWorked}h`}</Typography>
            )}

            {dayOff && !totalHoursWorked && (
              <AppTooltipContainer
                content={bankHolidayMark}
                title={dayOff.name}
              />
            )}
          </Button>
        </Paper>
      )}
    </Grid>
  );
}
